import { useOutletContext, Link } from "react-router-dom";
import { Card, CardContent, Typography, Button, Stack, Progress } from "@altimateai/lego";
import { IconFileText, IconAlertTriangle, IconLoader2, IconClock } from "@tabler/icons-react";
import { MdxMarkdownEditor } from "@/components/markdownEditor";
import { KnowledgeBaseDetailResponse } from "../types";
import {
  isFailedStatus,
  isProcessingStatus,
  toUserFriendlyError,
  getPhaseProgress,
  getProcessingTimeInfo,
} from "../utils/statusHelpers";

interface OutletContext {
  data?: KnowledgeBaseDetailResponse;
}

export default function DocumentTab() {
  const { data } = useOutletContext<OutletContext>();

  const hasContent = data?.content?.pages?.[0]?.content;
  const isFailed = isFailedStatus(data?.processing_status);
  const isProcessing = isProcessingStatus(data?.processing_status);

  // Get phase progress info
  const phaseProgress = getPhaseProgress(data?.processing_status);
  const timeInfo = getProcessingTimeInfo(
    data?.file_size_bytes || 0,
    data?.processing_status,
    data?.processing_started_at
  );

  return (
    <Card>
      <CardContent className="al-pt-4">
        {isFailed ? (
          <div className="al-py-20">
            <Stack direction="column" align="center" justify="center" gap="lg">
              <div className="al-rounded-full al-bg-red-100 al-p-6">
                <IconAlertTriangle className="al-w-10 al-h-10 al-text-red-600" />
              </div>
              <Stack direction="column" align="center" gap="sm">
                <Typography variant="h3" weight="semibold" className="al-text-gray-900">
                  Document processing failed
                </Typography>
                <Typography
                  variant="body"
                  size="sm"
                  className="al-text-gray-600 al-text-center al-max-w-lg"
                >
                  {toUserFriendlyError(data?.processing_error)}
                </Typography>
                <Button asChild variant="outline" className="al-mt-2">
                  <Link to="history">View History</Link>
                </Button>
              </Stack>
            </Stack>
          </div>
        ) : hasContent ? (
          <MdxMarkdownEditor markdown={data.content.pages[0].content} readOnly />
        ) : isProcessing ? (
          <div className="al-py-16">
            <Stack direction="column" align="center" justify="center" gap="lg">
              <div className="al-rounded-full al-bg-blue-100 al-p-6">
                <IconLoader2 className="al-w-10 al-h-10 al-text-blue-600 al-animate-spin" />
              </div>
              <Stack direction="column" align="center" gap="sm" className="al-w-full al-max-w-md">
                <Typography variant="h3" weight="semibold" className="al-text-gray-900">
                  Processing your document
                </Typography>

                {/* Phase Progress */}
                {phaseProgress && (
                  <Stack direction="column" gap="sm" className="al-w-full al-mt-4">
                    <Stack direction="row" justify="between" className="al-w-full">
                      <Typography variant="body" size="sm" className="al-text-gray-600">
                        {phaseProgress.phaseName}
                      </Typography>
                      <Typography variant="body" size="sm" className="al-text-gray-500">
                        Step {phaseProgress.currentPhase} of {phaseProgress.totalPhases}
                      </Typography>
                    </Stack>
                    <Progress value={phaseProgress.percentage} className="al-h-2" />
                  </Stack>
                )}

                {/* Time Info */}
                {timeInfo.showTimeInfo && (
                  <Stack direction="row" align="center" gap="xs" className="al-mt-2">
                    <IconClock className="al-w-4 al-h-4 al-text-gray-400" />
                    <Typography variant="body" size="sm" className="al-text-gray-500">
                      {timeInfo.elapsedText} • {timeInfo.estimateText}
                    </Typography>
                  </Stack>
                )}

                {timeInfo.isExceeding && (
                  <Typography variant="body" size="sm" className="al-text-amber-600 al-mt-1">
                    Taking longer than expected. Please wait...
                  </Typography>
                )}
              </Stack>
            </Stack>
          </div>
        ) : (
          <div className="al-py-20">
            <Stack direction="column" align="center" justify="center" gap="lg">
              <div className="al-rounded-full al-bg-blue-100 al-p-6">
                <IconFileText className="al-w-10 al-h-10 al-text-blue-600" />
              </div>
              <Stack direction="column" align="center" gap="sm">
                <Typography variant="h3" weight="semibold" className="al-text-gray-900">
                  Document is being processed
                </Typography>
                <Typography
                  variant="body"
                  size="regular"
                  className="al-text-gray-600 al-text-center al-max-w-lg"
                >
                  Your document is being parsed and will be available soon. Check the History tab to
                  view the processing status.
                </Typography>
              </Stack>
            </Stack>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
